import { usePostHog } from '@posthog/react';
import { PostHog } from 'posthog-js';
import { useEffect, useState } from 'react';

import { User } from '@/api/rest/resources/types/user';

import { AnalyticsEvent, POSTHOG_API_KEY, POSTHOG_BASE_URL } from '../constants';

let posthogInstance: PostHog | null = null;
let consentListeners: Array<(consent: boolean) => void> = [];

export const setAnalyticsConsent = (consent: boolean) => {
  consentListeners.forEach((listener) => listener(consent));
};

export const usePostHogInit = () => {
  const posthog = usePostHog();
  const [hasConsent, setHasConsent] = useState(false);

  // cookiebot triggers "CookiebotOnAccept" event when user gives consent for "statistics"
  // we listen for this event, update hasConsent
  // initialize PostHog only if we have user consent
  // below listeners connect the non-react event listening to the react state
  useEffect(() => {
    const listener = (consent: boolean) => setHasConsent(consent);
    consentListeners.push(listener);

    // reset listeners when session/browser's refreshed (acts like a new session)
    return () => {
      consentListeners = consentListeners.filter((l) => l !== listener);
    };
  }, []);

  useEffect(() => {
    if (POSTHOG_API_KEY && POSTHOG_BASE_URL && posthog) {
      posthogInstance = posthog;

      if (hasConsent) {
        posthogInstance.opt_in_capturing();
      } else {
        posthogInstance.opt_out_capturing();
      }
    }
  }, [posthog, hasConsent]);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const captureEvent = ({ name, params }: { name: AnalyticsEvent; params?: Record<string, any> }) => {
  posthogInstance?.capture(name, params);
};

export const identifyUser = (user: User, impersonatedBy?: string) => {
  posthogInstance?.identify(user.email, {
    id: user.id,
    email: user.email,
    firstname: user.firstname,
    lastname: user.lastname,
    is_staff: user.is_staff,
    impersonated_by: impersonatedBy,
    organization_id: user.membership?.organization.id ?? null,
    organization_name: user.membership?.organization.name ?? null,
    membership: user.membership,
  });
};
